import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject, getMetadata } from 'firebase/storage';

// Function to create a canvas with specific dimensions
const createCanvas = (width, height) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

// Function to validate crop area parameters
const validateCropArea = (croppedAreaPixels) => {
  if (!croppedAreaPixels) {
    throw new Error('Crop area is required');
  }

  const requiredProps = ['x', 'y', 'width', 'height'];
  const missingProps = requiredProps.filter(prop => 
    typeof croppedAreaPixels[prop] !== 'number' || isNaN(croppedAreaPixels[prop])
  );

  if (missingProps.length > 0) {
    throw new Error(`Invalid crop area: missing or invalid ${missingProps.join(', ')}`);
  }

  if (croppedAreaPixels.width <= 0 || croppedAreaPixels.height <= 0) {
    throw new Error('Crop area dimensions must be positive');
  }
};

// Function to compress and resize image
const processImage = async (
  imageSrc, 
  croppedAreaPixels, 
  targetWidth, 
  targetHeight, 
  quality = 0.7
) => {
  // Validate inputs
  if (!imageSrc) {
    throw new Error('Image source is required');
  }
  
  if (!targetWidth || !targetHeight) {
    throw new Error('Target dimensions are required');
  }

  // Validate crop area parameters
  validateCropArea(croppedAreaPixels);

  return new Promise((resolve, reject) => {
    const image = new Image();
    
    image.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    image.onload = () => {
      try {
        // Create canvas with target dimensions
        const canvas = createCanvas(targetWidth, targetHeight);
        const ctx = canvas.getContext('2d');

        // Draw the cropped area onto the canvas
        ctx.drawImage(
          image,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0,
          0,
          targetWidth,
          targetHeight
        );

        // Convert to blob with compression
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create image blob'));
              return;
            }
            resolve(blob);
          },
          'image/jpeg',
          quality
        );
      } catch (error) {
        reject(error);
      }
    };
    
    image.src = imageSrc;
  });
};

export const uploadProfileImage = async (
  file, 
  croppedAreaPixels, 
  userId,
  isProfile = true
) => {
  if (!file || !croppedAreaPixels || !userId) {
    throw new Error('Missing required parameters for upload');
  }

  try {
    console.log('Processing profile image with crop data:', croppedAreaPixels); // Debug log

    // Process image based on type
    const blob = await processImage(
      URL.createObjectURL(file),
      croppedAreaPixels,
      isProfile ? 500 : 1200,  // width
      isProfile ? 500 : 675,   // height
      0.7                      // quality
    );

    // Create file name
    const timestamp = Date.now();
    const fileName = `${isProfile ? 'profile' : 'cover'}_${timestamp}.jpg`;
    
    // Create reference
    const storage = getStorage();
    const imageRef = ref(storage, `users/${userId}/profileMedia/${fileName}`);

    // Upload processed image
    await uploadBytes(imageRef, blob);

    // Get download URL
    return await getDownloadURL(imageRef);
  } catch (error) {
    console.error('Error in uploadProfileImage:', error); // Debug log
    throw error;
  }
};

export const uploadBandImage = async (
  file, 
  croppedAreaPixels, 
  userId,
  bandId,
  isProfile = true
) => {
  if (!file || !croppedAreaPixels || !userId || !bandId) {
    throw new Error('Missing required parameters for upload');
  }

  try {
    console.log('Processing band image with crop data:', croppedAreaPixels); // Debug log

    // Process image based on type
    const blob = await processImage(
      URL.createObjectURL(file),
      croppedAreaPixels,
      isProfile ? 500 : 1200,  // width
      isProfile ? 500 : 675,   // height
      0.7                      // quality
    );

    // Use consistent file name (without timestamp) for easy replacement
    const fileName = isProfile ? 'profile.jpg' : 'cover.jpg';
    
    // Create reference with proper path structure
    const storage = getStorage();
    const storagePath = `bands/${bandId}/profileMedia/${fileName}`;
    const newImageRef = ref(storage, storagePath);

    // Upload the new image first to ensure we have it before deleting the old one
    await uploadBytes(newImageRef, blob);
    
    // Get download URL of the new image
    const downloadURL = await getDownloadURL(newImageRef);
    
    return downloadURL;
  } catch (error) {
    console.error('Error in uploadBandImage:', error); // Debug log
    throw error;
  }
};

export const deleteOldImage = async (imageUrl) => {
  if (!imageUrl) return;
  try {
    const storage = getStorage();
    // Extract the path from the URL
    const imagePath = decodeURIComponent(imageUrl.split('/o/')[1].split('?')[0]);
    const imageRef = ref(storage, imagePath);
    await deleteObject(imageRef);
  } catch (error) {
    console.error('Error deleting old image:', error);
    // Don't throw error as this is not critical
  }
};